import type { ActivityAction, ActivityResponse, ActivityRun } from "@contract/firm/activity";
import { Group, Loader, Select, Stack, Tabs, Text, TextInput } from "@mantine/core";
import * as React from "react";
import * as activityApi from "../api/activity";
import { useMe } from "../api/MeContext";
import { firmCanHaveTeam } from "../layout/nav";
import { SecondaryButton } from "../ui/buttons";
import { DataTable, type DataColumn } from "../ui/DataTable";
import { MetaText } from "../ui/MetaText";
import { Notice } from "../ui/Notice";
import { PageHeader } from "../ui/PageHeader";
import { PageLoader } from "../ui/PageLoader";
import { PANEL_SPACE } from "../ui/tokens";
import {
  actionLabel,
  coverageLabel,
  findingsLabel,
  memberLabel,
  memberText,
  NO_FILTERS,
  scopeLabel,
  skillLabel,
  whenLabel,
  type ActivityFilters,
} from "./activityModel";
import { ThisMonthCard } from "./home/ThisMonthCard";
import { useActivity } from "./useActivity";

type Api = typeof activityApi;

/** A cell's main line with a dimmed second line under it. */
function TwoLines({ main, sub }: { main: string; sub?: string | null }) {
  return (
    <Stack gap={2}>
      <Text lh={1.25}>{main}</Text>
      {sub ? (
        <Text fz="xs" c="dimmed" lh={1.25}>
          {sub}
        </Text>
      ) : null}
    </Stack>
  );
}

function runColumns(data: ActivityResponse): DataColumn<ActivityRun>[] {
  return [
    { key: "date", header: "Date", render: (r) => <Text lh={1.25} style={{ whiteSpace: "nowrap" }}>{whenLabel(r.at)}</Text> },
    { key: "member", header: "Member", render: (r) => memberLabel(data.members, r.userId) },
    { key: "skill", header: "Skill", render: (r) => <TwoLines main={skillLabel(r.skillId)} sub={r.skillVersion ? `Version ${r.skillVersion}` : null} /> },
    { key: "scope", header: "Scope", render: (r) => scopeLabel(r) },
    { key: "findings", header: "Findings", render: (r) => findingsLabel(r) },
    { key: "coverage", header: "Coverage", render: (r) => coverageLabel(r) },
  ];
}

function actionColumns(data: ActivityResponse): DataColumn<ActivityAction>[] {
  const cols: DataColumn<ActivityAction>[] = [
    { key: "date", header: "Date", render: (a) => <Text lh={1.25} style={{ whiteSpace: "nowrap" }}>{whenLabel(a.at)}</Text> },
    { key: "member", header: "Member", render: (a) => memberLabel(data.members, a.userId) },
    { key: "action", header: "Action", render: (a) => actionLabel(a) },
    { key: "issue", header: "Issue", render: (a) => <TwoLines main={a.issueId ? `#${a.issueId}` : ""} sub={a.skillId ? skillLabel(a.skillId) : null} /> },
  ];
  if (data.shareFindingText) cols.push({ key: "finding", header: "Finding", render: (a) => a.findingText ?? "" });
  return cols;
}

/** Activity Log (firm-model spec §9, §10.2; `view_activity`; no frame yet, built from the
 * panel library): what the firm's members ran and what they did about the issues it found.
 * Two tabs (runs, actions), server-side filters and pages. Members show by email. The
 * finding-text toggle is hidden for now (Dan 2026-10-02): finding text always shows; the
 * server setting stays, unused. Never chat threads or slides. */
export function ActivityPage({ api = activityApi, now = new Date() }: { api?: Api; now?: Date }) {
  const { state: meState } = useMe();
  const me = meState.status === "ready" ? meState.me : null;
  const [filters, setFilters] = React.useState<ActivityFilters>(NO_FILTERS);
  const { data, loading, loadError } = useActivity(api, filters);
  /** Any filter change goes back to the first page. */
  const change = (patch: Partial<ActivityFilters>) => setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));

  // Rows render by the kind the answer is for, so a kind switch never shows the old rows
  // under the new headings while the new page loads.
  const showRows = data && !loadError;

  return (
    <Stack gap={PANEL_SPACE.page}>
      <PageHeader title="Activity Log" aside={<MetaText>Chat threads and slides are never shown here.</MetaText>} />
      {/* D73's This Month, moved here from Home (Dan 2026-10-02). */}
      <ThisMonthCard now={now} showMembers={me ? firmCanHaveTeam(me) : false} load={api.fetchActivitySummary} />

      <Group gap={PANEL_SPACE.card} align="flex-end" wrap="wrap">
        <Select
          label="Member"
          placeholder="Everyone"
          clearable
          value={filters.member}
          onChange={(v) => change({ member: v })}
          data={(data?.members ?? []).map((m) => ({ value: m.userId, label: memberText(m) }))}
          w={240}
        />
        <Select
          label="Skill"
          placeholder="Every skill"
          clearable
          value={filters.skill}
          onChange={(v) => change({ skill: v })}
          data={(data?.skills ?? []).map((s) => ({ value: s, label: skillLabel(s) }))}
          w={200}
        />
        <TextInput label="From" type="date" value={filters.fromDay} onChange={(e) => change({ fromDay: e.currentTarget.value })} />
        <TextInput label="To" type="date" value={filters.toDay} onChange={(e) => change({ toDay: e.currentTarget.value })} />
        {loading && data ? <Loader aria-label="Loading" /> : null}
      </Group>

      <Tabs variant="outline" value={filters.kind} onChange={(v) => change({ kind: v === "actions" ? "actions" : "runs" })}>
        <Tabs.List pl={PANEL_SPACE.field}>
          <Tabs.Tab value="runs">Runs</Tabs.Tab>
          <Tabs.Tab value="actions">Actions</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {loadError ? <Notice tone="danger">{loadError}</Notice> : null}
      {!data && !loadError ? <PageLoader /> : null}
      {/* A failed reload keeps the last answer in `data`: show neither its rows nor its empty
          state under the error, which would read as the new filters' result. */}
      {showRows && data.kind === "runs" ? (
        <DataTable label="Runs" columns={runColumns(data)} rows={data.runs} rowKey={(r) => r.id} empty="No runs match these filters." />
      ) : null}
      {showRows && data.kind === "actions" ? (
        <DataTable label="Actions" columns={actionColumns(data)} rows={data.actions} rowKey={(a) => a.id} empty="No actions match these filters." />
      ) : null}

      {data && (filters.page > 1 || data.hasMore) ? (
        <Group gap="xs">
          <SecondaryButton size="xs" disabled={filters.page <= 1 || loading} onClick={() => change({ page: filters.page - 1 })}>
            Previous
          </SecondaryButton>
          <Text>Page {filters.page}</Text>
          <SecondaryButton size="xs" disabled={!data.hasMore || loading} onClick={() => change({ page: filters.page + 1 })}>
            Next
          </SecondaryButton>
        </Group>
      ) : null}
    </Stack>
  );
}
